import React, { useCallback, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button, useToast } from "@swordsweeper/ssi-ui-component-library";
import { useCurrentUserStore } from "@src/state/currentUserState";
import * as AuthApi from "@src/api/AuthApi";
import { DISTRIBUTOR_NAMES } from "@src/constants/Distributors";
import { addStaticRootToUrl } from "@src/utils/static";
import styles from "./TermsUpdatePage.module.scss";

export default function TermsUpdatePage(): React.JSX.Element {
  const { distributorConnections, fetchDistributorConnections } =
    useCurrentUserStore();
  const navigate = useNavigate();
  const showToast = useToast();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const pendingConnection =
    distributorConnections?.find((c) => c.needsTermsUpdate === true) ?? null;

  const handleAccept = useCallback(async () => {
    if (!pendingConnection) return;
    setIsAccepting(true);
    try {
      await AuthApi.reacceptDistributorTerms(pendingConnection.distributor);
      await fetchDistributorConnections();
      showToast({
        message: `Terms accepted for ${DISTRIBUTOR_NAMES[pendingConnection.distributor]}`,
        variant: "success",
      });
      navigate("/");
    } catch (error: any) {
      const errorMessage =
        error?.response?.body?.error ||
        "Error accepting terms. Please try again.";
      showToast({ message: errorMessage, variant: "error" });
    } finally {
      setIsAccepting(false);
    }
  }, [pendingConnection, fetchDistributorConnections, showToast, navigate]);

  const handleDisconnect = useCallback(async () => {
    if (!pendingConnection?.credentialId) return;
    setIsDisconnecting(true);
    try {
      await AuthApi.deleteDistributorAuthCredentials(
        pendingConnection.credentialId,
      );
      await fetchDistributorConnections();
      showToast({
        message: `Disconnected from ${DISTRIBUTOR_NAMES[pendingConnection.distributor]}`,
        variant: "success",
      });
      navigate("/");
    } catch (error: any) {
      showToast({
        message: "Error disconnecting from distributor",
        variant: "error",
      });
    } finally {
      setIsDisconnecting(false);
    }
  }, [pendingConnection, fetchDistributorConnections, showToast, navigate]);

  // Connections loaded with no pending update — guard against direct URL access
  if (distributorConnections !== null && !pendingConnection) {
    return <Navigate to="/" replace />;
  }

  // Connections still loading
  if (!pendingConnection) {
    return <div className={styles.page} />;
  }

  const distributorName = DISTRIBUTOR_NAMES[pendingConnection.distributor];
  const termsUrl = pendingConnection.currentTerms?.url;
  const termsName = pendingConnection.currentTerms?.name;

  return (
    <div className={styles.page}>
      <div className={styles.logoBar}>
        <Link to="/" className={styles.logoLink}>
          <img
            className={styles.logo}
            src={addStaticRootToUrl("images/logo_colored.png")}
            alt="OrderEasy Logo"
          />
        </Link>
      </div>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            {distributorName} has updated their Terms and Conditions
          </h1>
          <p className={styles.description}>
            Please review and accept the updated Terms and Conditions to
            continue.
          </p>
          <hr className={styles.divider} />
        </div>

        <div className={styles.contentArea}>
          <h2 className={styles.termsHeading}>
            {termsName ?? `${distributorName} Terms and Conditions`}
          </h2>
          {termsUrl && (
            <iframe
              src={termsUrl}
              className={styles.termsIframe}
              title={termsName ?? "Terms and Conditions"}
            />
          )}
        </div>

        <div className={styles.actions}>
          <Button
            className={`btn btn-primary btn-md ${styles.agreeButton}`}
            onClick={handleAccept}
            disabled={isAccepting || isDisconnecting}
          >
            {isAccepting ? "Accepting..." : "I Agree"}
          </Button>
          {pendingConnection.credentialId && (
            <Button
              className={`btn btn-link btn-md ${styles.disconnectButton}`}
              onClick={handleDisconnect}
              disabled={isAccepting || isDisconnecting}
            >
              {isDisconnecting
                ? "Disconnecting..."
                : `Disconnect from ${distributorName}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
